const CART_DRAWER_OPEN_CLASS = 'wl-cart-drawer-open';
const CART_DRAWER_EVENT_PREFIX = 'wl:cart-drawer';

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlayClose() {
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.boundClose);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.openWithFreshContents(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.openWithFreshContents(cartLink);
      }
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.refreshContentsFromServer();
    });
  }

  openWithFreshContents(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.refreshContentsFromServer()
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.open();
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty') || this.querySelector('.drawer__inner')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.setCartDrawerOpenState(true);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.setCartDrawerOpenState(false);
  }

  refreshContentsFromServer() {
    if (this.refreshContentsPromise) return this.refreshContentsPromise;

    const sectionsUrl = new URL(routes.cart_url, window.location.origin);
    sectionsUrl.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
    sectionsUrl.searchParams.set('sections_url', window.location.pathname);

    this.refreshContentsPromise = fetch(sectionsUrl.toString())
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((sections) => {
        this.renderSectionContents(sections);
      })
      .finally(() => {
        this.refreshContentsPromise = null;
      });

    return this.refreshContentsPromise;
  }

  setCartDrawerOpenState(isOpen) {
    document.documentElement.classList.toggle(CART_DRAWER_OPEN_CLASS, isOpen);
    document.body.classList.toggle(CART_DRAWER_OPEN_CLASS, isOpen);

    window.dispatchEvent(
      new CustomEvent(`${CART_DRAWER_EVENT_PREFIX}:${isOpen ? 'open' : 'close'}`, {
        detail: {
          drawer: this,
        },
      })
    );
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const shouldOpen = options.open !== false;

    this.updateEmptyState(parsedState);
    this.productId = parsedState.id;
    this.renderSectionContents(parsedState.sections);

    if (!shouldOpen) return;

    setTimeout(() => {
      this.bindOverlayClose();
      this.open();
    });
  }

  renderSectionContents(sections = {}) {
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = sections?.[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionInnerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);

      if (!sectionElement || sectionInnerHTML === null) return;
      sectionElement.innerHTML = sectionInnerHTML;
    });

    this.updateEmptyState({ sections });
    this.bindOverlayClose();
  }

  updateEmptyState(parsedState) {
    const drawerSection = this.getSectionDOM(parsedState.sections?.['cart-drawer'], 'cart-drawer');

    if (drawerSection) {
      this.classList.toggle('is-empty', drawerSection.classList.contains('is-empty'));
      return;
    }

    const itemCount = this.getItemCount(parsedState);
    if (itemCount !== null) this.classList.toggle('is-empty', itemCount === 0);
  }

  getItemCount(parsedState) {
    if (typeof parsedState.item_count === 'number') return parsedState.item_count;
    if (Array.isArray(parsedState.items)) {
      return parsedState.items.reduce((total, item) => total + Number(item.quantity || 0), 0);
    }
    if (typeof parsedState.quantity === 'number') return parsedState.quantity;

    return null;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
